// src/constants/ceremony.js
// ============================================================================
// EVENT CEREMONY — phase vocabulary + timing (founder spec 2026-08-06)
//
// The founder's sequence, verbatim:
//   1. registration closes -> countdown ticks for people to get online
//   2. T-5min: seeding begins, STARTING SOON b-roll starts, music playing
//   3. T-2min: ready modal pops for racers; spectators see the ready-up
//      animation, DYNAMIC per event and per IGN
//   4. T-30s: fade out b-roll -> fade in the live feeds, POV cycling
//   5. vision detects win/loss/quit per POV -> that POV fades to its result;
//      a QUIT gets "awaiting results" + the option to switch camera
//   6. all results in -> final Crits ceremony
//
// ---------------------------------------------------------------------------
// WHY THE CLOCK IS NOT THE SOURCE OF TRUTH
// ---------------------------------------------------------------------------
// The T-5/T-2/T-30 numbers describe how the sequence should FEEL. The system
// that actually stages an event is Match Ready, and its real beats are
// lifecycle events, not wall-clock offsets:
//   - registration closes -> the event LOCKS  (~15 min before start)
//   - lock -> matchReadySystem writes playersReadyState + readyDeadline
//     (readyDeadline = the start time itself, floor 2 min)
//   - all ready -> verification -> status live
// Driving the ceremony off wall-clock guesses would put the b-roll and the real
// ready modal on different clocks — the spectator would watch a "ready up now!"
// animation two minutes after the racers already readied.
//
// So: LIFECYCLE WINS, and the offsets below are the floor. Each phase starts at
// whichever comes first — its lifecycle signal, or its clock threshold. Nothing
// here changes when Match Ready fires; the ceremony only reacts to it.
// (See feedback-match-ready-is-platform-backbone: no lifecycle changes without
// founder sign-off.)
// ============================================================================

export const CEREMONY_PHASE = {
  OFF: 'off',                     // nothing to stage (too early, or disabled)
  COUNTDOWN: 'countdown',         // registration closed, clock ticking down
  STARTING_SOON: 'starting_soon', // b-roll + music + LIVE per-IGN ready ticker
  GRID_LOCK: 'grid_lock',         // field complete -> dice lock the grid
  HANDOFF: 'handoff',             // crossfading out to the live feeds
  LIVE: 'live',                   // feeds own the screen, no overlay
  RESULTS: 'results',             // final standings + Crits ceremony
};

// Per-POV state for the selected camera (founder step 5).
export const POV_STATE = {
  RACING: 'racing',
  FINISHED: 'finished',
  DNF: 'dnf',        // quit / disconnect -> "awaiting results" + switch camera
  REVIEW: 'review',  // terminal but vision wasn't sure; a human decides
};

// ---------------------------------------------------------------------------
// Timing (all ms). Tunable — these set the feel, not the lifecycle.
// ---------------------------------------------------------------------------

// T-5: b-roll comes up. Also the moment seeding normally fires.
export const STARTING_SOON_LEAD_MS = 5 * 60 * 1000;

// Nothing at all is staged before this; a spectator arriving 40 minutes early
// gets the ordinary watch page, not a five-minute countdown card.
export const COUNTDOWN_LEAD_MS = 30 * 60 * 1000;

// T-30: start dissolving the b-roll into the feeds.
export const HANDOFF_LEAD_MS = 30 * 1000;

// How long the crossfade itself runs, and how long GRID LOCKED holds before it.
export const HANDOFF_FADE_MS = 1200;
export const GRID_LOCK_HOLD_MS = 6500;

// A POV result card sits over that feed this long before releasing back to the
// picture (a quit/DNF holds until the event resolves — there's nothing to
// release back to).
export const POV_RESULT_HOLD_MS = 12000;

// Poll cadence for the public ceremony state. The ready ticker is the phase
// that has to feel live, so it polls hardest; LIVE only needs to notice a POV
// terminating; RESULTS is settled and barely moves.
export const POLL_MS = {
  [CEREMONY_PHASE.OFF]: 30000,
  [CEREMONY_PHASE.COUNTDOWN]: 15000,
  [CEREMONY_PHASE.STARTING_SOON]: 3000,
  [CEREMONY_PHASE.GRID_LOCK]: 3000,
  [CEREMONY_PHASE.HANDOFF]: 5000,
  [CEREMONY_PHASE.LIVE]: 6000,
  [CEREMONY_PHASE.RESULTS]: 20000,
};

// ---------------------------------------------------------------------------
// Brand (locked — reference-brand-identity). Kept here so every ceremony
// surface reads from one place and matches the approved demo screens.
// ---------------------------------------------------------------------------
export const CEREMONY_COLORS = {
  BG: '#000000',
  SURFACE: 'rgba(17,17,17,0.92)',
  BORDER: '#222222',
  GOLD: '#FCD000',
  GOLD_BRIGHT: '#ffe565',
  CYAN: '#29C4E8',
  CYAN_SOFT: '#9adfef',
  GREEN: '#7ee787',
  PURPLE: '#c792ff',
  TEXT: '#ededed',
  DIM: '#888888',
  DNF: '#b33333',
};

// Music. Static assets (like /media/hero-bg.mp4) rather than bundled require()s
// — ~3.8MB each and only the ceremony ever needs them, so they're fetched on
// first musical phase, never at page load.
//
// A ROTATION, not a single loop: a long countdown on one track wears thin, and
// the ceremony can sit in COUNTDOWN for up to 30 minutes. Tracks play in order
// and wrap; add to this list and the rotation grows with no other changes.
// ceremony-theme.mp3 IS Midnight_Redline (same master, renamed when it shipped).
export const CEREMONY_MUSIC_URLS = [
  '/media/ceremony-theme.mp3',     // Midnight Redline
  '/media/ceremony-velocity.mp3',  // Redline Velocity
];

// The hype backdrop for the pre-event beats: the CritCash banner video, looping
// under the countdown and STARTING SOON — a multi-game montage (GT7, Marvel
// Rivals, MOBA) that doubles as a showcase of what the platform runs.
//
// This is the SAME file the landing page already ships. hero-bg.mp4 is
// banner.mp4 transcoded to 720p/2.6Mbps (identical 30.866667s duration; it was
// encoded three minutes after the master). Reusing it costs zero additional
// bytes in the build and inherits a video path already proven to autoplay
// muted across browsers. The 78MB/125MB masters in
// CritCashDocuments/Media/ShowdownDemo/assets are edit sources, not web assets.
export const CEREMONY_BACKDROP_VIDEO_URL = '/media/hero-bg.mp4';
export const CEREMONY_BACKDROP_POSTER_URL = '/media/hero-bg-poster.jpg';

/**
 * Which phase a spectator should be seeing.
 *
 * @param {object} s   ceremony payload from getEventCeremony (+ nowMs already
 *                     corrected to local clock by the caller)
 * @param {number} now local ms
 * @param {boolean} hasLiveFeed  is there at least one resolvable stream
 * @param {boolean} feedReady    is a player actually PAINTING a frame
 * @returns {string} CEREMONY_PHASE
 */
export function computeCeremonyPhase(s, now, hasLiveFeed, feedReady = false) {
  if (!s || !s.found) return CEREMONY_PHASE.OFF;

  const status = s.status || '';

  // --- terminal ------------------------------------------------------------
  // Both spellings are in the wild: eventLifecycleTriggers writes 'cancelled',
  // admin.js and payouts.js write 'canceled'.
  if (status === 'cancelled' || status === 'canceled') return CEREMONY_PHASE.OFF;
  if (status === 'completed') return CEREMONY_PHASE.RESULTS;
  // The stuck-event safeguard flips an event to 'review_required'. The racing is
  // over and a human is deciding — that belongs on the results surface (which
  // labels itself PROVISIONAL until payouts settle), not on a live-feed handoff
  // that would sit there waiting for cameras that are never coming back.
  if (status === 'review_required') return CEREMONY_PHASE.RESULTS;

  const startsAt = s.startsAtMs || null;
  const untilStart = startsAt ? startsAt - now : null;

  // --- running -------------------------------------------------------------
  // Once the event is live the feeds own the screen — but ONLY once there is a
  // picture to hand over to.
  //
  // ⚠️ This gates on feedReady (a player is PAINTING), not hasLiveFeed (a URL
  // resolves). Handing over on resolution alone is what put a long black gap in
  // the middle of the first live run (2026-08-07, event 15mRI45TVDtBCO9Hb9Xq):
  // relay->HLS latency measured 34s, so the ceremony unmounted and uncovered a
  // player that had no frame yet. Resolution is not a picture.
  //
  // Two things make this bite on REAL events, not just the demo rig:
  //  1. any racer who starts streaming before startAt makes hasLiveFeed true
  //     early, so the b-roll would drop the instant the event goes live; and
  //  2. the event goes live EARLY — the lifecycle starts it once everyone is
  //     ready and verification clears (measured 69s before startAt on that
  //     run), and this branch is checked before every clock-based beat below,
  //     so an early go-live otherwise truncates STARTING SOON / GRID LOCK.
  //
  // Holding here is safe: CeremonyStage bounds the hold with
  // HANDOFF_MAX_HOLD_MS (90s), after which it gives the screen back and lets
  // the player's own "waiting for players to go live" placeholder tell the truth.
  if (status === 'live' || status === 'in_progress' || s.startedAtMs) {
    return feedReady ? CEREMONY_PHASE.LIVE : CEREMONY_PHASE.HANDOFF;
  }

  // readyStatus enum: waiting | partial | all_ready | verifying | verified |
  // verification_failed | timeout (+ off-enum 'awaiting_host' / 'skipped').
  // Everything from all_ready onward means the grid is set.
  const total = s.totalRequired || (s.field ? s.field.length : 0);
  const allReady = total > 0 && s.readyCount >= total;
  const locking = ['all_ready', 'verifying', 'verified'].includes(s.readyStatus);

  // --- T-30: handing off ---------------------------------------------------
  // The clock alone is NOT enough. Events routinely run past their scheduled
  // start (racers still readying, verification retrying, a host gate open), and
  // handing off on wall-clock alone pinned the ceremony to an invisible
  // dissolve the moment startAt passed — killing the ready ticker exactly when
  // spectators most want it. Require something real: a feed, or a set grid.
  if (untilStart != null && untilStart <= HANDOFF_LEAD_MS && (hasLiveFeed || allReady || locking)) {
    return CEREMONY_PHASE.HANDOFF;
  }

  // --- the field is set: lock the grid -------------------------------------
  if (allReady || locking) return CEREMONY_PHASE.GRID_LOCK;

  // --- T-5: STARTING SOON --------------------------------------------------
  // Registration closes at startAt−15min (REGISTRATION_CLOSE_LEAD_MIN), and
  // lock→seed→ready all fire there — so "the ready window is open" is a T−15
  // signal, NOT a T−5 one. Using it alone would put the b-roll up ten minutes
  // early. The clock leads here; lifecycle is the safety net for a late lock
  // (ready window open with under 5 min left) and for the server's own T−5
  // milestone (eventReminderQueue stamps reminder5MinSent).
  const readyOpen = !!s.readyDeadlineMs && s.readyDeadlineMs > now;
  const lateLock = readyOpen && untilStart != null && untilStart <= STARTING_SOON_LEAD_MS;
  const noStartTime = readyOpen && untilStart == null;
  if (
    (untilStart != null && untilStart <= STARTING_SOON_LEAD_MS)
    || lateLock
    || noStartTime
    || s.reminder5MinSent === true
  ) {
    return CEREMONY_PHASE.STARTING_SOON;
  }

  // --- registration closed, waiting (founder step 1) ----------------------
  const locked = !!s.lockedAtMs
    || status === 'locked'
    || status === 'seeded'
    || readyOpen
    || s.lifecyclePhase === 'ready_confirmation'
    || (!!s.registrationClosesAtMs && s.registrationClosesAtMs <= now);
  if (locked || (untilStart != null && untilStart <= COUNTDOWN_LEAD_MS)) {
    return CEREMONY_PHASE.COUNTDOWN;
  }

  return CEREMONY_PHASE.OFF;
}

/** Human countdown, e.g. "4:07" / "1:02:11". */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}
